import * as Yup from 'yup'
import { isValid as isValidCpf, strip as stripCpf } from '@fnando/cpf'
import { isValid as isValidCnpj, strip as stripCnpj } from '@fnando/cnpj'
import BankingService from 'services/Banking'

const onlyDigits = value => (value ?? '').replace (/\D/g, '')

/*
  A chave chega `undefined` enquanto o campo não foi tocado, e o Yup roda os testes assim
  mesmo — o `required` não os curto-circuita. Sem esta função, escolher "E-mail" ou "Chave
  aleatória" antes de digitar derrubava a tela inteira com `Cannot read properties of
  undefined (reading 'trim')`. Os outros três tipos escapavam por acaso, porque `strip` e
  `onlyDigits` já toleram nada.
*/
const trimmed = value => (value ?? '').trim ()

/*
  Cada tipo de chave tem um formato próprio e uma mensagem própria: dizer só "chave
  inválida" deixa o titular sem saber se errou o número ou o tipo selecionado.
*/
const KEY_VALIDATORS = {
  CPF: {
    message: 'Insira um CPF válido',
    test: value => isValidCpf (stripCpf (value))
  },
  CNPJ: {
    message: 'Insira um CNPJ válido',
    test: value => isValidCnpj (stripCnpj (value))
  },
  EMAIL: {
    message: 'Insira um e-mail válido',
    test: value => BankingService.EMAIL_PATTERN.test (trimmed (value))
  },
  PHONE: {
    message: 'Insira um celular válido com DDD',
    test: value => {
      const digits = onlyDigits (value)
      const national = digits.startsWith ('55') && digits.length > 11
        ? digits.slice (2)
        : digits

      return [10, 11].includes (national.length)
    }
  },
  EVP: {
    message: 'Insira uma chave aleatória válida',
    test: value => BankingService.EVP_PATTERN.test (trimmed (value))
  }
}

/*
  O formulário é de uma etapa só. A Asaas não expõe consulta de chave Pix de terceiro
  (DICT), então não há como mostrar o nome de quem vai receber antes do envio — a
  conferência possível é a da própria chave digitada, feita aqui e repetida no diálogo de
  confirmação.

  É por isso que a validação de formato de cada tipo de chave abaixo importa mais do que
  pareceria: ela é a única defesa contra um Pix enviado para a chave errada, que não volta.
*/
const bankingPixTransferValidator = Yup.object ().shape ({
  availableBalance: Yup.number ().default (0),

  keyType: Yup.object ()
    .nullable ()
    .required ('Selecione o tipo de chave'),

  key: Yup.string ()
    .required ('Insira a chave Pix')
    .test ('pix-key-format', 'Chave Pix inválida', function (value) {
      const type = this.parent.keyType?.value
      const validator = KEY_VALIDATORS[type]

      // Sem tipo escolhido não há formato a conferir — o erro aparece no campo do tipo.
      if (!validator) return true

      return validator.test (value) || this.createError ({message: validator.message})
    }),

  value: Yup.number ()
    .typeError ('Insira o valor da transferência')
    .positive ('Insira o valor da transferência')
    .max (Yup.ref ('availableBalance'), 'O valor é maior que o seu saldo disponível')
    .required ('Insira o valor da transferência'),

  description: Yup.string ()
    .max (120, 'A descrição deve ter no máximo 120 caracteres')
})

export default bankingPixTransferValidator
